import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Standard Table Pagination Component
 *
 * @param {Object} props
 * @param {Object} props.pagination - Pagination state object
 * @param {number} props.pagination.pageIndex - Current page index (0-indexed)
 * @param {number} props.pagination.pageSize - Current page size
 * @param {number} props.pagination.total - Total number of records
 * @param {number} props.pagination.totalPages - Total number of pages
 * @param {boolean} props.pagination.hasPrevPage - Whether there is a previous page
 * @param {boolean} props.pagination.hasNextPage - Whether there is a next page
 * @param {Function} props.onPageChange - Callback for page change (receives new 0-indexed pageIndex)
 * @param {Function} props.onPageSizeChange - Callback for page size change
 */
const TablePagination = ({ pagination, onPageChange, onPageSizeChange }) => {
  const { pageIndex, pageSize, total, totalPages, hasPrevPage, hasNextPage } =
    pagination;

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      // First page
      pages.push(0);

      if (pageIndex > 3) {
        pages.push('ellipsis1');
      }

      const start = Math.max(1, pageIndex - 1);
      const end = Math.min(totalPages - 2, pageIndex + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (pageIndex < totalPages - 4) {
        pages.push('ellipsis2');
      }

      // Last page
      if (!pages.includes(totalPages - 1)) {
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  return (
    <div className="flex-shrink-0 bg-white px-4 py-2 border-t border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        {/* Mobile Pagination */}
        <div className="flex flex-col gap-3 sm:hidden">
          {/* Page Size Selector */}
          <div className="flex items-center justify-center space-x-2">
            <label htmlFor="page-size-mobile" className="text-sm text-gray-700">
              Show:
            </label>
            <select
              id="page-size-mobile"
              value={pageSize}
              onChange={e => onPageSizeChange(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {[5, 10, 20, 50].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500">per page</span>
          </div>
          <div className="flex justify-between">
            <button
              onClick={() => onPageChange(pageIndex - 1)}
              disabled={!hasPrevPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(pageIndex + 1)}
              disabled={!hasNextPage}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Desktop Pagination */}
        <div className="hidden sm:flex sm:items-center sm:justify-between w-full">
          <div className="flex items-center space-x-6">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {total > 0 ? pageIndex * pageSize + 1 : 0}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min((pageIndex + 1) * pageSize, total)}
                </span>{' '}
                of <span className="font-medium">{total}</span> results
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Page Size Selector */}
            <div className="flex items-center space-x-2">
              <label htmlFor="page-size" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="page-size"
                value={pageSize}
                onChange={e => onPageSizeChange(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[5, 10, 20, 50].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-500">per page</span>
            </div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => onPageChange(pageIndex - 1)}
                disabled={!hasPrevPage}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeft className="h-5 w-5" />
              </button>

              {/* Page Numbers */}
              {getPageNumbers().map((p, i) => {
                if (typeof p === 'string') {
                  return (
                    <span
                      key={`ellipsis-${i}`}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      p === pageIndex
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {p + 1}
                  </button>
                );
              })}

              <button
                onClick={() => onPageChange(pageIndex + 1)}
                disabled={!hasNextPage}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
